import { classes } from "polytype";
import { Money } from "../../base/Money.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { provideReact, type AsyncResourceVariant } from "../../react/index.js";
import type {
  ArticleAttribute,
  ArticleData,
  ArticleListItemData,
  ArticleListQueryModelData,
  ArticleTag,
  MachineTypeOptimization,
} from "./types.js";

export class Article extends ReferenceModel {
  public static ofId(id: string) {
    return new Article(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.article.find(id);

    if (data !== undefined) {
      return new ArticleDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const article = await this.find(id);
    assertObjectFound(article, "article", id);
    return article;
  });

  public getDetailed = provideReact(() => {
    return Article.get(this.id);
  }, [this.id]) as AsyncResourceVariant<() => Promise<ArticleDetailed>>;

  public findDetailed = provideReact(() => {
    return Article.get(this.id);
  }, [this.id]) as AsyncResourceVariant<
    () => Promise<ArticleDetailed | undefined>
  >;

  public static queryWithTagFilter(
    tagName: string,
    baseQuery: ArticleListQueryModelData = {},
  ) {
    return new ArticleListQuery({
      ...baseQuery,
      resourceFilter: tagName,
    });
  }

  public static query(query: ArticleListQueryModelData = {}) {
    return new ArticleListQuery(query);
  }
}

class ArticleCommon extends classes(
  DataModel<ArticleListItemData | ArticleData>,
  Article,
) {
  public readonly attributes?: ArticleAttribute[];
  public readonly price: Money;
  public readonly name: string;
  public readonly tags?: ArticleTag[];
  public readonly description?: string;
  public readonly contractDurationInMonth: number;
  public readonly forcedInvoicingPeriodInMonth?: number;
  public readonly hideOnInvoice?: boolean;
  public readonly hasIndependentContractPeriod?: boolean;
  public readonly orderable:
    | "forbidden"
    | "internal"
    | "full"
    | "deprecated"
    | "beta_testing";
  public readonly machineTypeOptimization?: MachineTypeOptimization;
  public readonly vcpu?: number;
  public readonly ram?: number;
  public readonly machineType?: string;
  public readonly storage?: number;

  public constructor(data: ArticleListItemData | ArticleData) {
    super([data], [data.articleId]);
    this.attributes = data.attributes;
    this.price = Money({ amount: data.price, currency: "EUR" });
    this.description = data.description;
    this.orderable = data.orderable;
    this.tags = data.tags;
    this.name = data.name;
    this.contractDurationInMonth = data.contractDurationInMonth;
    this.forcedInvoicingPeriodInMonth = data.forcedInvoicingPeriodInMonth;
    this.hideOnInvoice = data.hideOnInvoice;
    this.hasIndependentContractPeriod = data.hasIndependentContractPeriod;
    // id is used as fallback for proSpace lite
    this.machineType =
      this.name !== "proSpace lite" && this.name !== "Webhosting"
        ? this.attributes?.find(
            (a) => a.key === "machine_type" || a.key === "spec.machine_type",
          )?.value
        : this.id;
    this.machineTypeOptimization = data.tags?.find(
      (t) =>
        t.name === "balance-optimized" ||
        t.name === "ram-optimized" ||
        t.name === "cpu-optimized",
    )?.name as MachineTypeOptimization;
    const vcpuString = this.attributes?.find((a) => a.key === "vcpu")?.value;
    if (vcpuString) {
      this.vcpu = parseInt(vcpuString);
    }
    const ramString = this.attributes?.find((a) => a.key === "ram")?.value;
    if (ramString) {
      this.ram = parseInt(ramString);
    }
    const storageString = this.attributes?.find(
      (a) => a.key === "storage",
    )?.value;
    if (storageString) {
      this.storage = parseInt(storageString);
    }
  }
}

export class ArticleDetailed extends classes(
  ArticleCommon,
  DataModel<ArticleData>,
) {
  public constructor(data: ArticleData) {
    super([data], [data]);
  }
}

export class ArticleListItem extends classes(
  ArticleCommon,
  DataModel<ArticleListItemData>,
) {
  public constructor(data: ArticleListItemData) {
    super([data]);
  }
}

export class ArticleListQuery extends ListQueryModel<ArticleListQueryModelData> {
  public constructor(query: ArticleListQueryModelData = {}) {
    super(query);
  }

  public refine(query: ArticleListQueryModelData) {
    return new ArticleListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { ...query } = this.query;
    let { items, totalCount } = await config.behaviors.article.list({
      limit: config.defaultPaginationLimit,
      ...query,
    });

    if (query.resourceFilter) {
      items = items.filter((article) => {
        if (article.tags && Array.isArray(article.tags)) {
          return article.tags.some(
            (tag: ArticleTag) => tag.name === query.resourceFilter,
          );
        }
        return true;
      });
      totalCount = items.length;
    }

    return new ArticleList(
      this.query,
      items.map((d) => new ArticleListItem(d)),
      totalCount,
    );
  }, [this.queryId, "article"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "article"]);
}

export class ArticleList extends classes(
  ArticleListQuery,
  ListDataModel<ArticleListItem>,
) {
  public constructor(
    query: ArticleListQueryModelData,
    articles: ArticleListItem[],
    totalCount: number,
  ) {
    super([query], [articles, totalCount]);
  }

  public static filterByTag(
    articles: ArticleListItem[],
    tagName: string,
  ): ArticleListItem[] {
    return articles.filter((article) => {
      if (article.tags && Array.isArray(article.tags)) {
        return article.tags.some((tag: ArticleTag) => tag.name === tagName);
      }
      return false;
    });
  }
}
